// Get a photo ready for upload: shrink it to a sensible size and save it as a JPEG, in the browser,
// so phone photos (often 6 to 12 MB, sometimes HEIC) just work. Throws an Error with a plain-language
// message when a photo cannot be used, so callers can show it next to the upload button.
const MB = 1024 * 1024;
export const MAX_UPLOAD_BYTES = 5 * MB;
const KEEP_AS_IS_BYTES = 1.5 * MB;

export const fmtSize = (bytes) => (bytes >= MB ? `${(bytes / MB).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`);

const loadBitmap = async (file) => {
  if (typeof createImageBitmap === 'function') {
    try { return await createImageBitmap(file); } catch { /* fall back to an <img> element */ }
  }
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('decode')); };
    img.src = url;
  });
};

const toJpegBlob = (bitmap, width, height, quality) => new Promise((resolve) => {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = '#ffffff'; // flatten transparency (PNG) onto white
  ctx.fillRect(0, 0, width, height);
  ctx.drawImage(bitmap, 0, 0, width, height);
  canvas.toBlob(resolve, 'image/jpeg', quality);
});

/**
 * @param {File} file the photo the person picked
 * @param {{maxDimension?: number, maxBytes?: number}} options longest side in pixels, and the size limit
 * @returns {Promise<{file: File, note: string|null}>}
 */
export const prepareImage = async (file, { maxDimension = 1600, maxBytes = MAX_UPLOAD_BYTES } = {}) => {
  if (!file) throw new Error('No photo selected.');
  const looksLikeImage = (file.type || '').startsWith('image/') || /\.(heic|heif|jpe?g|png|webp|gif)$/i.test(file.name || '');
  if (!looksLikeImage) throw new Error('That file is not a photo. Please choose a JPG, PNG or WebP image.');

  let bitmap;
  try {
    bitmap = await loadBitmap(file);
  } catch {
    throw new Error("We couldn't read that photo. If it came from an iPhone, open it in Photos and export it as a JPEG, or choose a different photo.");
  }
  const width = bitmap.naturalWidth || bitmap.width;
  const height = bitmap.naturalHeight || bitmap.height;
  const scale = Math.min(1, maxDimension / Math.max(width, height));

  // Already a sensible web image: keep the original bytes so quality isn't lost twice.
  if (scale === 1 && ['image/jpeg', 'image/png', 'image/webp'].includes(file.type) && file.size <= KEEP_AS_IS_BYTES) {
    bitmap.close?.();
    return { file, note: null };
  }

  let w = Math.round(width * scale);
  let h = Math.round(height * scale);
  let quality = 0.85;
  let blob = null;
  for (let attempt = 0; attempt < 5; attempt += 1) {
    blob = await toJpegBlob(bitmap, w, h, quality); // eslint-disable-line no-await-in-loop
    if (blob && blob.size <= maxBytes) break;
    quality = Math.max(0.55, quality - 0.1);
    if (attempt >= 1) { w = Math.round(w * 0.85); h = Math.round(h * 0.85); }
  }
  bitmap.close?.();
  if (!blob || blob.size > maxBytes) {
    throw new Error(`That photo is still ${fmtSize(blob ? blob.size : file.size)} after shrinking and the limit is ${fmtSize(maxBytes)}. Please choose a simpler photo.`);
  }
  const base = (file.name || 'photo').replace(/\.[^.]+$/, '') || 'photo';
  const out = new File([blob], `${base}.jpg`, { type: 'image/jpeg' });
  return { file: out, note: `Photo prepared: ${fmtSize(file.size)} to ${fmtSize(out.size)}.` };
};
